import { useState, useCallback, useRef } from 'react';
import { useToast } from '@/hooks/use-toast';
import { generatePDFFromElement, downloadBlob, getMaterialFilename } from '@/services/materialGenerationService';

export const useBrochurePDFExport = () => {
  const [isExporting, setIsExporting] = useState(false);
  const { toast } = useToast();

  const exportBrochureAsPDF = useCallback(async (
    brochureRef: React.RefObject<HTMLDivElement>,
    filename?: string
  ) => {
    if (!brochureRef.current) {
      toast({
        title: "Export Error",
        description: "Brochure content not ready. Please try again.",
        variant: "destructive",
      });
      return null;
    }

    setIsExporting(true);

    try {
      toast({
        title: "Generating PDF",
        description: "Please wait while we create your brochure...",
      });

      // Generate PDF from the brochure element
      const pdfBlob = await generatePDFFromElement(
        brochureRef.current,
        filename || 'equiforge-brochure.pdf',
        {
          scale: 1.5,
          orientation: 'portrait',
          format: 'letter',
        }
      );

      // Download the PDF
      const finalFilename = filename || getMaterialFilename('one-pager', 'pdf');
      downloadBlob(pdfBlob, finalFilename);

      toast({
        title: "Success",
        description: "Brochure PDF generated successfully!",
      });

      return finalFilename;
    } catch (error) {
      console.error('PDF export error:', error);
      toast({
        title: "Export Failed",
        description: error instanceof Error ? error.message : "Failed to generate PDF",
        variant: "destructive",
      });
      return null;
    } finally {
      setIsExporting(false);
    }
  }, [toast]);

  return {
    exportBrochureAsPDF,
    isExporting,
  };
};
